// BCM-898 · Goods Receipt Dashboard — container (slides 3, 4, 5, 34, 35). [Uday]
//
// A sibling of Batch Execution (same batches, same workflow, same statuses, new
// surface). Deliberately isolated from Batch Execution: it holds batch data in
// LOCAL state (never the shared sasl Redux slice), so Batch Execution is
// zero-regression. GDS users land here; every other role reaches it from the
// Collaboration menu.
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import BackDrop from "../models/BackDrop";
import featuresAndValidations from "../models/featuresAccess/FeaturesAndValidations";
import { getFeaturesAccess } from "../../../containers/actions/common/accessControl";

import FilterBar from "./components/FilterBar";
import BatchTable from "./components/BatchTable";
import useGoodsReceiptBatches from "./hooks/useGoodsReceiptBatches";
import GRFeedbackProvider from "./context/GRFeedbackContext";
import { GENERIC_ERROR } from "./utils/constants";
import { useGRStyles } from "./goodsReceiptStyles";

const GoodsReceiptDashboard = (props) => {
  const classes = useGRStyles();
  const dispatch = useDispatch();
  const params = (props.match && props.match.params) || {};

  const [selectedCMOList, setSelectedCMOList] = useState([]);
  const [tempSearch, setTempSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const {
    batches,
    loading,
    loadingMore,
    error,
    hasFetched,
    hasMore,
    lastSynced,
    fetch,
    loadMore,
    refresh,
  } = useGoodsReceiptBatches();

  // ── Access control ───────────────────────────────────────────────────────
  // D1: goodsReceiptDashboard does not exist in the screen map yet — alias
  // deliverySchedule in dev so the screen is reachable.
  const screenAccess = useSelector(
    (state) =>
      (state.pcmp.common.accessControl.screensAccess &&
        state.pcmp.common.accessControl.screensAccess.data &&
        state.pcmp.common.accessControl.screensAccess.data.deliverySchedule) ||
      null
  );
  const featuresAccess = useSelector(
    (state) =>
      state.pcmp.common.accessControl.featuresAccess.deliverySchedulesFeatures
  );
  const qualityApprovalFlowFlag = useSelector(
    (state) =>
      state.pcmp.common.reports.rmpmGrnFeatures &&
      state.pcmp.common.reports.rmpmGrnFeatures.data &&
      state.pcmp.common.reports.rmpmGrnFeatures.data.features &&
      state.pcmp.common.reports.rmpmGrnFeatures.data.features.qualityApprovalEnabled
  );
  const cmoMemberWithId = useSelector((state) =>
    state.pcmp.pharmaCo.purchaseOrder.cmoMemberWithId
      ? state.pcmp.pharmaCo.purchaseOrder.cmoMemberWithId
      : []
  );

  useEffect(() => {
    if (screenAccess && screenAccess.screenAccess && !featuresAccess) {
      dispatch(
        getFeaturesAccess(screenAccess.screenId, "deliverySchedulesFeatures")
      );
    }
  }, [dispatch, screenAccess, featuresAccess]);

  // ── Deep link: /goodsReceipt/:batchNo prefills and runs a search (slide 5). ─
  useEffect(() => {
    if (params.batchNo) {
      setTempSearch(params.batchNo);
      setSearchValue(params.batchNo.trim());
    }
  }, [params.batchNo]);

  // ── Fetch whenever the CMO selection or search value changes. No pagination —
  // every matching batch is fetched in a single call. ───────────────────────
  useEffect(() => {
    fetch({ cmoList: selectedCMOList, searchValue });
  }, [fetch, selectedCMOList, searchValue]);

  // CMOFilterMultiSelect calls back on close with an event-like { target: { value } }.
  const handleSelectCMO = (event) => {
    setSelectedCMOList((event && event.target && event.target.value) || []);
  };
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setTempSearch(value);
    if (value === "" || value === null) setSearchValue("");
  };
  const handleClearSearch = (event) => {
    if (event) event.preventDefault();
    setTempSearch("");
    setSearchValue("");
  };
  const submitSearch = (event) => {
    if (event) event.preventDefault();
    setSearchValue((tempSearch || "").trim());
  };
  const handleRefresh = useCallback(() => refresh(), [refresh]);

  const nothingSelected =
    selectedCMOList.length === 0 && (!searchValue || searchValue.trim() === "");

  return (
    <GRFeedbackProvider>
      <BackDrop />
      <div className={classes.root}>
        <FilterBar
          selectedCMOList={selectedCMOList}
          filteredCMOList={cmoMemberWithId}
          onSelectCMO={handleSelectCMO}
          tempSearch={tempSearch}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          onSubmitSearch={submitSearch}
          lastSynced={lastSynced}
          onRefresh={handleRefresh}
          loading={loading}
        />

        <Divider style={{ margin: "1rem 0" }} />

        {error && (
          <div className={classes.errorLine}>
            <WarningRoundedIcon style={{ fontSize: 18 }} />
            {GENERIC_ERROR}
          </div>
        )}

        {nothingSelected ? (
          <div className={classes.emptyState}>
            Select a CMO or search a PO / Batch Number to view received batches.
          </div>
        ) : (
          <BatchTable
            batches={batches}
            loading={loading}
            loadingMore={loadingMore}
            hasFetched={hasFetched}
            hasMore={hasMore}
            loadMore={loadMore}
            featuresAccess={featuresAccess}
            qualityApprovalFlowFlag={qualityApprovalFlowFlag}
            onRefresh={handleRefresh}
          />
        )}
      </div>
    </GRFeedbackProvider>
  );
};

export default featuresAndValidations(GoodsReceiptDashboard);
